package com.mgacreative.hotel.feature.admin.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.shared.ui.designsystem.components.HFCard
import com.mgacreative.hotel.shared.ui.designsystem.components.VisualDatePicker
import com.mgacreative.hotel.shared.ui.designsystem.components.toDisplayDate
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import org.koin.compose.viewmodel.koinViewModel
import com.mgacreative.hotel.domain.model.PricePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@Composable
fun PricingManagementScreen(onNavigateBack: () -> Unit = {}) {
    val viewModel = koinViewModel<AdminPricingViewModel>()
    val periods by viewModel.pricePeriods.collectAsState()
    val roomTypes = viewModel.roomTypes
    
    var newPeriodName by remember { mutableStateOf("") }
    var newPeriodStart by remember { mutableStateOf("") }
    var newPeriodEnd by remember { mutableStateOf("") }
    
    // Map to hold prices for each room type
    val roomPrices = remember { mutableStateMapOf<String, String>() }

    // Custom DatePicker states
    var showStartDatePicker by remember { mutableStateOf(false) }
    var showEndDatePicker by remember { mutableStateOf(false) }

    // Edit/Delete Dialog states
    var selectedPeriodForEdit by remember { mutableStateOf<PricePeriod?>(null) }
    var editPeriodName by remember { mutableStateOf("") }
    var editPeriodStart by remember { mutableStateOf("") }
    var editPeriodEnd by remember { mutableStateOf("") }
    var editPeriodPrice by remember { mutableStateOf("") }
    
    var showEditStartDatePicker by remember { mutableStateOf(false) }
    var showEditEndDatePicker by remember { mutableStateOf(false) }

    // Bulk Delete Confirmation Dialog states
    var showBulkDeleteConfirm by remember { mutableStateOf<List<PricePeriod>?>(null) }

    LaunchedEffect(Unit) {
        viewModel.loadData()
        // Initialize room prices
        roomTypes.forEach { roomPrices[it] = "" }
    }
    
    // Group periods by name, start, and end
    val groupedPeriods = periods.groupBy { "${it.name}_${it.startDate}_${it.endDate}" }
    
    var formError by remember { mutableStateOf<String?>(null) }
    var formSuccess by remember { mutableStateOf<String?>(null) }

    fun submitPeriod() {
        val pricesMap = mutableMapOf<String, Double>()
        roomPrices.forEach { (type, priceStr) ->
            priceStr.toDoubleOrNull()?.let { pricesMap[type] = it }
        }
        
        if (newPeriodName.isBlank()) {
            formError = "Lütfen periyot adını girin (Örn: Yaz Sezonu)."
            formSuccess = null
            return
        }
        if (newPeriodStart.isBlank() || newPeriodEnd.isBlank()) {
            formError = "Lütfen başlangıç ve bitiş tarihlerini seçin."
            formSuccess = null
            return
        }
        if (pricesMap.isEmpty()) {
            formError = "Lütfen en az bir oda tipi için geçerli fiyat girin."
            formSuccess = null
            return
        }

        formError = null
        viewModel.addBulkPricePeriods(
            name = newPeriodName.trim(),
            start = newPeriodStart,
            end = newPeriodEnd,
            prices = pricesMap
        )
        newPeriodName = ""
        newPeriodStart = ""
        newPeriodEnd = ""
        roomTypes.forEach { roomPrices[it] = "" }
        formSuccess = "Periyot ve fiyatlar başarıyla kaydedildi."
    }

    val pageScrollState = rememberScrollState()

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFFF8FAFC))
            .verticalScroll(pageScrollState)
            .verticalScrollbar(pageScrollState)
            .padding(20.dp),
        verticalArrangement = Arrangement.spacedBy(20.dp)
    ) {
        // Top Form Header with Prominent Action Button
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = androidx.compose.ui.Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = "Toplu Periyot Fiyatı Ekle", 
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                    color = Color(0xFF0F172A)
                )
                Text(
                    text = "Tarih aralığı ve oda tiplerine göre sezonluk fiyat tarifesi oluşturun",
                    style = MaterialTheme.typography.bodySmall,
                    color = Color(0xFF64748B)
                )
            }

            Spacer(modifier = Modifier.width(16.dp))

            Button(
                onClick = { submitPeriod() },
                shape = androidx.compose.foundation.shape.RoundedCornerShape(8.dp),
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF1D4ED8)),
                contentPadding = PaddingValues(horizontal = 18.dp, vertical = 12.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Add,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = "Periyot ve Fiyat Ekle",
                    fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                    fontSize = 13.sp
                )
            }
        }

        // Form Card
        Surface(
            shape = androidx.compose.foundation.shape.RoundedCornerShape(12.dp),
            color = Color.White,
            border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(
                modifier = Modifier.padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                if (formError != null) {
                    Surface(
                        shape = androidx.compose.foundation.shape.RoundedCornerShape(8.dp),
                        color = Color(0xFFFEE2E2),
                        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFFECACA)),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            text = formError ?: "",
                            color = Color(0xFFDC2626),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = androidx.compose.ui.text.font.FontWeight.Medium,
                            modifier = Modifier.padding(12.dp)
                        )
                    }
                }

                if (formSuccess != null) {
                    Surface(
                        shape = androidx.compose.foundation.shape.RoundedCornerShape(8.dp),
                        color = Color(0xFFD1FAE5),
                        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFA7F3D0)),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            text = formSuccess ?: "",
                            color = Color(0xFF047857),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = androidx.compose.ui.text.font.FontWeight.Medium,
                            modifier = Modifier.padding(12.dp)
                        )
                    }
                }

                // Row 1: Period Name & Dates
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    OutlinedTextField(
                        value = newPeriodName,
                        onValueChange = { 
                            newPeriodName = it 
                            formError = null
                        },
                        label = { Text("Periyot Adı (Örn: Yaz Sezonu)") },
                        singleLine = true,
                        modifier = Modifier.weight(1.5f)
                    )

                    Box(modifier = Modifier.weight(1f)) {
                        OutlinedTextField(
                            value = if (newPeriodStart.isNotBlank()) newPeriodStart.toDisplayDate() else "",
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Başlangıç Tarihi") },
                            modifier = Modifier.fillMaxWidth()
                        )
                        Box(modifier = Modifier.matchParentSize().clickable { showStartDatePicker = true })
                    }

                    Box(modifier = Modifier.weight(1f)) {
                        OutlinedTextField(
                            value = if (newPeriodEnd.isNotBlank()) newPeriodEnd.toDisplayDate() else "",
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Bitiş Tarihi") },
                            modifier = Modifier.fillMaxWidth()
                        )
                        Box(modifier = Modifier.matchParentSize().clickable { showEndDatePicker = true })
                    }
                }

                HorizontalDivider(color = Color(0xFFE2E8F0))

                // Row 2: Room Types in Multi-Column Responsive Grid
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(
                        text = "Oda Tipleri ve Fiyatları ($)",
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                        color = Color(0xFF334155)
                    )

                    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                        val columns = if (maxWidth > 900.dp) 3 else if (maxWidth > 600.dp) 2 else 1

                        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            roomTypes.chunked(columns).forEach { rowTypes ->
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                                ) {
                                    rowTypes.forEach { type ->
                                        Surface(
                                            shape = androidx.compose.foundation.shape.RoundedCornerShape(8.dp),
                                            color = Color(0xFFF8FAFC),
                                            border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                                            modifier = Modifier.weight(1f)
                                        ) {
                                            Row(
                                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                                                verticalAlignment = androidx.compose.ui.Alignment.CenterVertically,
                                                horizontalArrangement = Arrangement.SpaceBetween
                                            ) {
                                                Text(
                                                    text = type,
                                                    style = MaterialTheme.typography.bodyMedium,
                                                    fontWeight = androidx.compose.ui.text.font.FontWeight.Medium,
                                                    color = Color(0xFF1E293B),
                                                    modifier = Modifier.weight(1f)
                                                )
                                                OutlinedTextField(
                                                    value = roomPrices[type] ?: "",
                                                    onValueChange = { 
                                                        roomPrices[type] = it 
                                                        formError = null
                                                    },
                                                    placeholder = { Text("0.00") },
                                                    prefix = { Text("$", color = Color(0xFF94A3B8)) },
                                                    singleLine = true,
                                                    modifier = Modifier.width(110.dp)
                                                )
                                            }
                                        }
                                    }
                                    val remaining = columns - rowTypes.size
                                    if (remaining > 0) {
                                        repeat(remaining) {
                                            Spacer(modifier = Modifier.weight(1f))
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                HorizontalDivider(color = Color(0xFFE2E8F0))

                // Bottom Action Button Inside Card
                Button(
                    onClick = { submitPeriod() },
                    shape = androidx.compose.foundation.shape.RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF1D4ED8)),
                    modifier = Modifier.fillMaxWidth().height(48.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.Add,
                        contentDescription = null,
                        modifier = Modifier.size(18.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = "Periyot ve Fiyatları Kaydet",
                        fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                        fontSize = 14.sp
                    )
                }
            }
        }

        // Section: Existing Saved Periods
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(
                text = "Kayıtlı Fiyat Periyotları (${groupedPeriods.size})",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                color = Color(0xFF0F172A)
            )

            if (groupedPeriods.isEmpty()) {
                Surface(
                    shape = androidx.compose.foundation.shape.RoundedCornerShape(10.dp),
                    color = Color.White,
                    border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = "Henüz kayıtlı fiyat periyodu bulunmuyor. Yukarıdaki formdan yeni bir periyot ekleyebilirsiniz.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = Color(0xFF64748B),
                        modifier = Modifier.padding(24.dp)
                    )
                }
            } else {
                groupedPeriods.values.forEach { group ->
                    val firstPeriod = group.firstOrNull()
                    if (firstPeriod != null) {
                        var expanded by remember { mutableStateOf(false) }
                        
                        Surface(
                            shape = androidx.compose.foundation.shape.RoundedCornerShape(10.dp),
                            color = Color.White,
                            border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable { expanded = !expanded },
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = androidx.compose.ui.Alignment.CenterVertically
                                ) {
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text(
                                            text = firstPeriod.name, 
                                            style = MaterialTheme.typography.titleMedium,
                                            fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                                            color = Color(0xFF1D4ED8)
                                        )
                                        Text(
                                            text = "${firstPeriod.startDate.toDisplayDate()} - ${firstPeriod.endDate.toDisplayDate()} • ${group.size} Oda Tipi", 
                                            style = MaterialTheme.typography.bodySmall,
                                            color = Color(0xFF64748B)
                                        )
                                    }
                                    
                                    Row(verticalAlignment = androidx.compose.ui.Alignment.CenterVertically) {
                                        IconButton(
                                            onClick = { showBulkDeleteConfirm = group },
                                            modifier = Modifier.size(32.dp)
                                        ) {
                                            Icon(
                                                Icons.Default.Delete, 
                                                contentDescription = "Tüm Periyodu Sil",
                                                tint = Color(0xFFDC2626)
                                            )
                                        }
                                        Spacer(modifier = Modifier.width(8.dp))
                                        Icon(
                                            if (expanded) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown,
                                            contentDescription = "Genişlet/Daralt",
                                            tint = Color(0xFF64748B)
                                        )
                                    }
                                }
                                
                                if (expanded) {
                                    Spacer(modifier = Modifier.height(12.dp))
                                    HorizontalDivider(color = Color(0xFFE2E8F0))
                                    Spacer(modifier = Modifier.height(12.dp))
                                    
                                    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                                        group.forEach { period ->
                                            Surface(
                                                shape = androidx.compose.foundation.shape.RoundedCornerShape(6.dp),
                                                color = Color(0xFFF8FAFC),
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .clickable {
                                                        selectedPeriodForEdit = period
                                                        editPeriodName = period.name
                                                        editPeriodStart = period.startDate
                                                        editPeriodEnd = period.endDate
                                                        editPeriodPrice = period.price.toString()
                                                    }
                                            ) {
                                                Row(
                                                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                                                    horizontalArrangement = Arrangement.SpaceBetween,
                                                    verticalAlignment = androidx.compose.ui.Alignment.CenterVertically
                                                ) {
                                                    Text(
                                                        text = period.roomType, 
                                                        style = MaterialTheme.typography.bodyMedium,
                                                        color = Color(0xFF334155)
                                                    )
                                                    Text(
                                                        text = "$${period.price}", 
                                                        style = MaterialTheme.typography.bodyMedium,
                                                        fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                                                        color = Color(0xFF059669)
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        Spacer(modifier = Modifier.height(24.dp))
    }

    // Bulk Delete Confirmation
    if (showBulkDeleteConfirm != null) {
        AlertDialog(
            onDismissRequest = { showBulkDeleteConfirm = null },
            title = { Text("Tüm Periyodu Sil") },
            text = { Text("Bu periyottaki tüm oda fiyatları kalıcı olarak silinecek. Onaylıyor musunuz?") },
            confirmButton = {
                Button(
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error),
                    onClick = {
                        val ids = showBulkDeleteConfirm!!.map { it.id }
                        viewModel.deletePricePeriods(ids)
                        showBulkDeleteConfirm = null
                    }
                ) {
                    Text("Toplu Sil")
                }
            },
            dismissButton = {
                TextButton(onClick = { showBulkDeleteConfirm = null }) {
                    Text("İptal")
                }
            }
        )
    }

    // Visual Date Picker Dialogs
    if (showStartDatePicker) {
        val initial = try { LocalDate.parse(newPeriodStart) } catch(e:Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
        VisualDatePicker(
            initialDate = initial,
            onDismissRequest = { showStartDatePicker = false },
            onDateSelected = { date -> 
                newPeriodStart = date.toString()
                showStartDatePicker = false
            }
        )
    }

    if (showEndDatePicker) {
        val initial = try { LocalDate.parse(newPeriodEnd) } catch(e:Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
        VisualDatePicker(
            initialDate = initial,
            onDismissRequest = { showEndDatePicker = false },
            onDateSelected = { date -> 
                newPeriodEnd = date.toString()
                showEndDatePicker = false
            }
        )
    }
    
    if (showEditStartDatePicker) {
        val initial = try { LocalDate.parse(editPeriodStart) } catch(e:Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
        VisualDatePicker(
            initialDate = initial,
            onDismissRequest = { showEditStartDatePicker = false },
            onDateSelected = { date -> 
                editPeriodStart = date.toString()
                showEditStartDatePicker = false
            }
        )
    }

    if (showEditEndDatePicker) {
        val initial = try { LocalDate.parse(editPeriodEnd) } catch(e:Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
        VisualDatePicker(
            initialDate = initial,
            onDismissRequest = { showEditEndDatePicker = false },
            onDateSelected = { date -> 
                editPeriodEnd = date.toString()
                showEditEndDatePicker = false
            }
        )
    }

    // Edit/Delete Dialog for individual room price inside a period
    if (selectedPeriodForEdit != null) {
        AlertDialog(
            onDismissRequest = { selectedPeriodForEdit = null },
            title = { Text("${selectedPeriodForEdit?.roomType} Düzenle") },
            text = {
                Column {
                    OutlinedTextField(
                        value = editPeriodName,
                        onValueChange = { editPeriodName = it },
                        label = { Text("Periyot Adı") },
                        modifier = Modifier.fillMaxWidth()
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Row(modifier = Modifier.fillMaxWidth()) {
                        Box(modifier = Modifier.weight(1f)) {
                            OutlinedTextField(
                                value = if (editPeriodStart.isNotBlank()) editPeriodStart.toDisplayDate() else "",
                                onValueChange = {},
                                readOnly = true,
                                label = { Text("Başlangıç") },
                                modifier = Modifier.fillMaxWidth()
                            )
                            Box(modifier = Modifier.matchParentSize().clickable { showEditStartDatePicker = true })
                        }
                        Spacer(modifier = Modifier.width(8.dp))
                        Box(modifier = Modifier.weight(1f)) {
                            OutlinedTextField(
                                value = if (editPeriodEnd.isNotBlank()) editPeriodEnd.toDisplayDate() else "",
                                onValueChange = {},
                                readOnly = true,
                                label = { Text("Bitiş") },
                                modifier = Modifier.fillMaxWidth()
                            )
                            Box(modifier = Modifier.matchParentSize().clickable { showEditEndDatePicker = true })
                        }
                    }
                    Spacer(modifier = Modifier.height(8.dp))
                    OutlinedTextField(
                        value = editPeriodPrice,
                        onValueChange = { editPeriodPrice = it },
                        label = { Text("Fiyat ($)") },
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        val price = editPeriodPrice.toDoubleOrNull()
                        if (editPeriodName.isNotBlank() && editPeriodStart.isNotBlank() && editPeriodEnd.isNotBlank() && price != null) {
                            selectedPeriodForEdit?.let { period ->
                                viewModel.updatePricePeriod(period.id, editPeriodName, editPeriodStart, editPeriodEnd, price)
                            }
                            selectedPeriodForEdit = null
                        }
                    }
                ) {
                    Text("Güncelle")
                }
            },
            dismissButton = {
                TextButton(
                    onClick = {
                        selectedPeriodForEdit?.let { period ->
                            viewModel.deletePricePeriod(period.id)
                        }
                        selectedPeriodForEdit = null
                    },
                    colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error)
                ) {
                    Text("Sil")
                }
            }
        )
    }
}
